import type { KeyboardEvent, ReactNode } from "react";

import { Chip } from "@/components/ds";
import { flowArrow, formatCurrency } from "@/utils/formatters";

export type MovementFlow = "income" | "expense" | "transfer";

/** Grid compartido por la cabecera y las filas (solo desde md). */
const MOVEMENT_GRID =
  "md:grid-cols-[minmax(0,1.8fr)_140px_110px_minmax(0,1fr)_120px_44px] md:gap-4";

const FLOW_STYLE: Record<MovementFlow, { sign: string; color: string }> = {
  income: { sign: "+", color: "text-[var(--app-success)]" },
  expense: { sign: "-", color: "text-[var(--app-danger)]" },
  transfer: { sign: "", color: "text-[var(--ds-ink-muted)]" },
};

export interface MovementTableLabels {
  movement: string;
  category: string;
  tag: string;
  account: string;
  amount: string;
  actions: string;
}

/** Encabezado de columnas de la tabla (solo desde md). */
export function MovementTableHead({ labels }: { labels: MovementTableLabels }) {
  return (
    <div
      aria-hidden="true"
      className={`hidden px-4 pt-5 text-[11px] font-medium uppercase leading-[14px] tracking-[0.14em] text-[var(--ds-ink-muted)] md:grid ${MOVEMENT_GRID}`}
    >
      <span>{labels.movement}</span>
      <span>{labels.category}</span>
      <span>{labels.tag}</span>
      <span>{labels.account}</span>
      <span className="text-right">{labels.amount}</span>
      <span>
        <span className="sr-only">{labels.actions}</span>
      </span>
    </div>
  );
}

export interface MovementGroupProps {
  /** Rótulo del día, p. ej. "Hoy" o "12 sep". */
  label: string;
  /** Total del día con signo: ≥ 0 se muestra en verde, < 0 en rojo, con flecha. */
  total: number;
  children: ReactNode;
}

/** Grupo por día: rótulo a la izquierda y total del día a la derecha. */
export function MovementGroup({ label, total, children }: MovementGroupProps) {
  const positive = total >= 0;
  return (
    <section aria-label={label} className="flex flex-col gap-2">
      <div className="mt-6 flex items-center justify-between gap-3">
        <h3 className="m-0 text-[11px] font-medium uppercase leading-[14px] tracking-[0.18em] text-[var(--ds-ink-muted)]">{label}</h3>
        <span className={`whitespace-nowrap text-[13px] font-medium leading-[14px] tabular-nums ${positive ? "text-[var(--app-success)]" : "text-[var(--app-danger)]"}`}>
          <span aria-hidden="true">{flowArrow(positive ? "income" : "expense")} </span>
          {`${positive ? "+" : "-"}${formatCurrency(Math.abs(total))}`}
        </span>
      </div>
      {children}
    </section>
  );
}

export interface MovementRowProps {
  id: string;
  name: string;
  /** Línea secundaria (móvil y tabla), p. ej. "Tarjeta · 14:20". */
  meta?: string;
  category?: { label: string; /** Cualquier valor CSS de color, p. ej. "var(--ds-card-mist)". */ colorVar?: string };
  tag?: string;
  account: string;
  /** Magnitud del monto (positiva); el signo y la flecha los pone `flow`. */
  amount: number;
  flow: MovementFlow;
  /** Fila recién creada: fondo de resalte. */
  highlighted?: boolean;
  selected?: boolean;
  selectable?: boolean;
  onClick(): void;
  /** Acciones de la fila (última columna); no disparan onClick. */
  actions?: ReactNode;
  flowSpoken: Record<MovementFlow, string>;
}

const stopRowEvent = (event: { stopPropagation(): void }) => event.stopPropagation();

/** Movimiento. Móvil: nombre y meta a la izquierda, monto a la derecha. Desde md: columnas de la tabla. */
export function MovementRow({
  name, meta, category, tag, account, amount, flow, highlighted, selected, selectable, onClick, actions, flowSpoken,
}: MovementRowProps) {
  const style = FLOW_STYLE[flow];
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={selectable ? Boolean(selected) : undefined}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      className={[
        "app-focus-ring flex cursor-pointer items-center gap-3 rounded-[var(--ds-radius-lg)] bg-[var(--ds-surface-raised)] p-3 text-[var(--ds-ink)] md:min-h-16 md:px-4 md:py-3 md:grid",
        MOVEMENT_GRID,
        highlighted && "bg-[var(--ds-card-butter)]",
        selected && "shadow-[inset_0_0_0_1.5px_var(--ds-ink)]",
      ].filter(Boolean).join(" ")}
    >
      <div className="min-w-0 flex-1 md:flex-none">
        <span className="block truncate text-base font-medium leading-[22px]">{name}</span>
        {meta ? <span className="block truncate text-[13px] leading-[18px] text-[var(--ds-ink-muted)]">{meta}</span> : null}
      </div>

      <div className="hidden min-w-0 md:block">
        {category ? (
          <Chip variant="filled" size="sm" color={category.colorVar}>
            {category.label}
          </Chip>
        ) : null}
      </div>

      <div className="hidden min-w-0 md:block">
        {tag ? (
          <Chip variant="outline" size="sm">
            {tag}
          </Chip>
        ) : (
          <span className="text-sm leading-5 text-[var(--ds-ink-muted)]">—</span>
        )}
      </div>

      <div className="hidden truncate text-sm leading-5 md:block">{account}</div>

      <div className={`flex-none whitespace-nowrap text-base font-medium leading-6 tabular-nums md:text-right ${style.color}`}>
        <span className="sr-only">{flowSpoken[flow]}: </span>
        <span aria-hidden="true">{flowArrow(flow)} </span>
        {`${style.sign}${formatCurrency(Math.abs(amount))}`}
      </div>

      <div className="flex flex-none items-center justify-end" onClick={stopRowEvent} onKeyDown={stopRowEvent}>
        {actions}
      </div>
    </div>
  );
}
